/**
 * Module dependencies.
 */

import { FormField } from 'app/components/atoms/forms/form-field/form-field';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Button } from 'app/components/atoms/button/button';
import { useCrudForm } from 'app/components/organisms/crud/crud-form-provider';
import { ShowRelations } from './show-relations';
import Checkbox from 'antd/lib/checkbox/Checkbox';
import { CCISearchInput } from './cci-search-input';

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
  flex: 1;
  width: 100%;
  align-items: center;
`;

/**
 * `StyledFormField` styled component.
 */

const StyledFormField = styled(FormField)`
  max-width: initial;
  width: 100%;
  flex: 1;
`;

/**
 * Default severity value.
 */

const DEFAULT_SEVERITY = true;

/**
 * Export `DrugsAssociationField` component.
 */

export function DrugsAssociationField(): JSX.Element {
  const [translate] = useTranslation();
  const [cciA, setCCIA] = useState<number | null>(null);
  const [cciB, setCCIB] = useState<number | null>(null);
  const [severity, setSeverity] = useState<boolean>(DEFAULT_SEVERITY);
  const form = useCrudForm();
  const { setValue, getValues } = form;
  const handleSubmit = useCallback(() => {
    const data = getValues('relations') ?? [];
    const found = data.find((relation: { cciA: number; cciB: number }) => {
      return relation.cciA === cciA && relation.cciB === cciB;
    });

    if (!!found) {
      setCCIA(null);
      setCCIB(null);
      setSeverity(DEFAULT_SEVERITY);

      return;
    }

    setValue('relations', [{ cciA: cciA, cciB: cciB, severity }, ...data]);
    setCCIA(null);
    setCCIB(null);
    setSeverity(DEFAULT_SEVERITY);
  }, [cciA, cciB, getValues, setValue, severity]);

  const deleteRelation = useCallback(
    (cciA: number, cciB: number) => {
      const relations = getValues('relations') ?? [];
      const updatedRelations = relations.filter(
        (relation: { cciA: number; cciB: number }) => !(relation.cciA === cciA && relation.cciB === cciB)
      );

      console.log(cciA, cciB, updatedRelations);

      setValue('relations', updatedRelations);
    },
    [getValues, setValue]
  );

  return (
    <StyledFormField label={translate('common.labels.ccisAssociation')}>
      <Wrapper>
        <CCISearchInput name={'cciA'} onChange={setCCIA} value={cciA} />

        <CCISearchInput name={'cciB'} onChange={setCCIB} value={cciB} />

        <Checkbox
          checked={severity}
          onChange={value => {
            setSeverity(value.target.checked);
          }}
        >
          {translate('cciAssociation.labels.severity')}
        </Checkbox>
      </Wrapper>

      <Button disabled={!cciA || !cciB} onClick={handleSubmit} type={'primary'}>
        {translate('common.actions.add')}
      </Button>

      <ShowRelations deleteRelation={deleteRelation} />
    </StyledFormField>
  );
}
